import React, {
    ChangeEvent,
    DetailedHTMLProps,
    FC,
    forwardRef,
    InputHTMLAttributes,
    useEffect,
    useState,
} from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'
import { LockFill } from '../../../assets/icons'

type RuleNames = 'label' | 'input' | 'container' | 'span' | 'lock'

interface StyleProps {
    query: string
    hasError: boolean
    isDatePicker?: boolean
}

export type InputProps = {
    label: string
    required: boolean
    useDisabled?: boolean
    hasError?: boolean
    useFloats?: boolean
    isDatePicker?: boolean
    onChangeHandler?: (v: string) => void
} & Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'size'
>

const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    container: {
        position: 'relative',
        display: 'block',
    },
    label: {
        position: 'relative',
        width: '100%',
        '&:focus-within > span': {
            color: colors.green500,
            transform: 'translateY(-15px)',
            fontSize: '0.75rem',
        },
        color: ({ hasError }) => (hasError ? colors.error : colors.labelColor),
    },
    input: {
        minHeight: '48px',
        // border: ({ hasError }) =>
        //     `1px solid ${hasError ? colors.error : colors.borderColor}`,
        borderRadius: '6px',
        width: '100%',
        paddingLeft: '0.75rem',
        // backgroundColor: ({ hasError }) => (hasError ? colors.errorBg : 'none'),

        paddingTop: ({ isDatePicker }) => (isDatePicker ? '12px' : '0'),

        '& + span': {
            transform: ({ isDatePicker }) =>
                isDatePicker ? 'translateY(-15px)' : 'none',
            color: ({ isDatePicker }) =>
                isDatePicker ? colors.green500 : colors.labelColor,
            fontSize: ({ isDatePicker }) => (isDatePicker ? '0.75rem' : '1rem'),
        },

        '&::placeholder': {
            opacity: '0',
        },
        color: colors.black,
        '&:not(:placeholder-shown) + span': {
            color: colors.green500,
            transform: 'translateY(-15px)',
            fontSize: '0.75rem',
        },
        '&:not(:placeholder-shown)': {
            paddingTop: '12px',
        },
        //  backgroundColor: ({ hasError }) => (hasError ? colors.errorBg : 'none'),
        '&:focus': {
            borderColor: ({ hasError }) =>
                hasError ? colors.error : colors.green500,
            outlineColor: ({ hasError }) =>
                hasError ? colors.error : colors.green500,
            paddingTop: '12px',
        },
        '&:active': {
            borderColor: colors.green500,
            outlineColor: colors.green500,
            paddingTop: '12px',
        },
        '&:disabled': {
            backgroundColor: '#F5F6F8',
            color: colors.black,
            '& + span': {
                color: colors.black,
            },
        },
    },
    span: {
        position: 'absolute',
        top: '0',
        left: '.75rem',
        fontSize: '1rem',
        transitionDuration: '300ms',
        color: colors.labelColor,
    },
    lock: {
        position: 'absolute',
        top: '0',
        right: '10px',
        opacity: '.5',
    },
})

export const Input: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            required,
            type,
            useDisabled,
            hasError,
            isDatePicker,
            ...props
        },
        ref
    ) => {
        const [query] = useState<string>('')
        const theme = useTheme<Jss.Theme>()
        const [error, setError] = useState(false)
        const classes = useStyles({
            ...{ query, hasError: error, isDatePicker },
            theme,
        })

        useEffect(() => {
            setError(hasError)
        }, [hasError])

        return (
            <div className={classes.container}>
                <label htmlFor={label} className={classes.label}>
                    <input
                        type={type || 'text'}
                        id={label}
                        placeholder={label}
                        className={classes.input}
                        {...props}
                        ref={ref}
                        required={required}
                        autoComplete="off"
                        style={{
                            ...props.style,
                            ...{
                                // background: error
                                //     ? colors.errorBg
                                //     : colors.white,
                                border: `1px solid ${
                                    hasError ? colors.error : colors.borderColor
                                }`,
                            },
                        }}
                    />
                    <span
                        className={classes.span}
                        style={{
                            color: hasError ? colors.error : colors.labelColor,
                        }}
                    >
                        {label} {required && <span> *</span>}
                    </span>
                    {props.disabled && useDisabled ? (
                        <span className={classes.lock}>
                            <LockFill width="16px" fill={colors.gray900} />
                        </span>
                    ) : null}
                </label>
            </div>
        )
    }
)

Input.defaultProps = {
    useDisabled: true,
    useFloats: false,
    hasError: false,
    isDatePicker: false,
}

export const InputNumber: FC<InputProps> = forwardRef<
    HTMLInputElement,
    InputProps
>(({ label, required, type, onChange, hasError, useFloats, ...props }, ref) => {
    const [query] = useState<string>('')
    const theme = useTheme<Jss.Theme>()
    const [error, setError] = useState(false)
    const classes = useStyles({ ...{ query, hasError: error }, theme })

    useEffect(() => {
        setError(hasError)
    }, [hasError])

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
        let newValue = event.target.value
        const modified = event
        if (useFloats) {
            newValue = newValue.replace(/[^0-9.,]/g, '')
            newValue = newValue
                .replace(/\./, '#')
                .replace(/\./g, '')
                .replace(/#/, '.')
            modified.target.value = newValue
        } else {
            modified.target.value = newValue.replace(/[^0-9]/g, '')
        }

        if (onChange) {
            onChange(modified)
        }
    }

    return (
        <div className={classes.container}>
            <label htmlFor={label} className={classes.label}>
                <input
                    type={type || 'text'}
                    id={label}
                    placeholder={label}
                    className={classes.input}
                    {...props}
                    ref={ref}
                    required={required}
                    onChange={handleChange}
                    autoComplete="off"
                    style={{
                        ...props.style,
                        ...{
                            // background: error ? colors.errorBg : colors.white,
                            border: `1px solid ${
                                hasError ? colors.error : colors.borderColor
                            }`,
                        },
                    }}
                />
                <span className={classes.span}>
                    {label} {required && <span> *</span>}
                </span>
                {props.disabled ? (
                    <span className={classes.lock}>
                        <LockFill width="16px" fill={colors.gray900} />
                    </span>
                ) : null}
            </label>
        </div>
    )
})

InputNumber.defaultProps = {
    useFloats: false,
}

export default Input
